import { useCallback, useEffect, useRef, useState } from "react";
import { Edit3, Image as ImageIcon, RefreshCw, Trash2, Upload } from "lucide-react";
import { api, apiUpload, tokenUrl } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { MarkerEditor } from "@/components/facilities/MarkerEditor";
import type { FacilityMap, GenericResponse } from "@/lib/types";

export function MapsPage() {
  const [maps, setMaps] = useState<FacilityMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [uploadName, setUploadName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const replaceRef = useRef<HTMLInputElement>(null);
  // Ziel-Map merkt sich ein Ref statt State: bricht der Nutzer den
  // Datei-Dialog ab, feuert kein change-Event — es darf dann kein
  // Spinner/Disabled-Zustand hängen bleiben.
  const replaceTargetRef = useRef<number | null>(null);
  const [replacingId, setReplacingId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get<{ maps: FacilityMap[] }>("/api/maps");
      setMaps(resp.maps ?? []);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    const name = uploadName.trim();
    if (!file || !name) return;
    setUploading(true);
    setFeedback(null);
    try {
      const form = new FormData();
      form.append("name", name);
      form.append("image", file);
      await apiUpload<FacilityMap>("/api/maps", form);
      setFeedback({ kind: "success", text: t("Plan „{name}\" hochgeladen.", { name }) });
      setUploadName("");
      if (fileRef.current) fileRef.current.value = "";
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Upload fehlgeschlagen") });
    } finally {
      setUploading(false);
    }
  };

  const onReplacePick = (m: FacilityMap) => {
    replaceTargetRef.current = m.id;
    replaceRef.current?.click();
  };

  const onReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const id = replaceTargetRef.current;
    e.target.value = "";
    replaceTargetRef.current = null;
    if (!file || id == null) return;
    setReplacingId(id);
    setFeedback(null);
    try {
      const form = new FormData();
      form.append("image", file);
      await apiUpload(`/api/maps/${id}/image`, form, "PUT");
      setFeedback({ kind: "success", text: t("Bild ersetzt. Marker bleiben erhalten.") });
      await reload();
    } catch (err) {
      setFeedback({ kind: "error", text: err instanceof Error ? err.message : t("Ersetzen fehlgeschlagen") });
    } finally {
      setReplacingId(null);
    }
  };

  const onDelete = async (m: FacilityMap) => {
    if (!confirm(t("Plan „{name}\" wirklich löschen? Marker werden mit gelöscht.", { name: m.name }))) return;
    try {
      await api.delete<GenericResponse>(`/api/maps/${m.id}`);
      setFeedback({ kind: "success", text: t("Plan „{name}\" gelöscht.", { name: m.name }) });
      if (editingId === m.id) setEditingId(null);
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Löschen fehlgeschlagen") });
    }
  };

  const editing = editingId != null ? maps.find((m) => m.id === editingId) : null;

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("Plan hochladen")}
        description={t("SVG, PNG, JPG oder WebP — max. 10 MB. SVG behält Schärfe beim Zoomen.")}
      >
        <form onSubmit={onUpload} className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto]">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Name")}
            </span>
            <input
              type="text"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder={t("z.B. Werk Süd – Halle 2")}
              required
              className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Bild")}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              required
              className="h-9 rounded-md border border-border bg-background px-2 py-1 text-xs file:mr-2 file:rounded file:border-0 file:bg-muted file:px-2 file:py-1 file:text-xs file:hover:bg-muted/70"
            />
          </label>
          <button
            type="submit"
            disabled={uploading}
            className="inline-flex h-9 items-center gap-1.5 self-end rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
          >
            <Upload className="h-3.5 w-3.5" /> {uploading ? t("Lade…") : t("Hochladen")}
          </button>
        </form>
      </Card>

      <Card title={t("Bestehende Pläne ({n})", { n: maps.length })}>
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : maps.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t("Noch keine Pläne hochgeladen.")}
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {maps.map((m) => (
              <li
                key={m.id}
                className={
                  "flex flex-col gap-2 rounded-md border bg-card p-3 " +
                  (editingId === m.id ? "border-primary" : "border-border")
                }
              >
                <div className="flex h-24 items-center justify-center overflow-hidden rounded bg-muted/50">
                  <img
                    src={tokenUrl(`/api/maps/${m.id}/image?v=${encodeURIComponent(m.image_path)}`)}
                    alt={m.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{m.name}</div>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <ImageIcon className="h-3 w-3" />
                      {m.width_px && m.height_px ? `${m.width_px}×${m.height_px}` : "—"}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setEditingId(editingId === m.id ? null : m.id)}
                      className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-muted"
                    >
                      <Edit3 className="h-3 w-3" /> {t("Marker")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onReplacePick(m)}
                      disabled={replacingId != null}
                      aria-label={t("Bild von Plan {name} ersetzen", { name: m.name })}
                      title={t("Bild ersetzen")}
                      className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-60"
                    >
                      <RefreshCw className={"h-3.5 w-3.5" + (replacingId === m.id ? " animate-spin" : "")} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(m)}
                      aria-label={t("Plan {name} löschen", { name: m.name })}
                      className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Unsichtbarer Datei-Dialog für „Bild ersetzen" */}
      <input
        ref={replaceRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={onReplaceFile}
      />

      {editing ? (
        <MarkerEditor map={editing} onClose={() => setEditingId(null)} onSaved={() => undefined} />
      ) : null}
    </div>
  );
}
